import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { colors } from '../../theme/theme';
import { useAuthService } from '../../hooks/useAuthService';
import { clearSession, landingPath } from '../../auth/session';

const OTP_RESEND_TIMER = 30;

const SLIDES = [
  {
    image: 'https://images.unsplash.com/photo-1556741533-6e6a62bd8b49?w=1200&q=80',
    tag: 'Queue Management System',
    title: 'Manage Queues Effortlessly.',
    highlight: 'Smart tokens. Happy customers',
    description:
      'Digital queue management, real-time token tracking, and seamless customer experience — all in one powerful platform.',
  },
  {
    image: 'https://images.unsplash.com/photo-1577962917302-cd874c4e31d2?w=1200&q=80',
    tag: 'Queue Management System',
    title: 'Reduce Wait Times.',
    highlight: 'Boost satisfaction',
    description:
      'Let your customers book tokens in advance, track their position in queue, and arrive just in time for their turn.',
  },
  {
    image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1200&q=80',
    tag: 'Queue Management System',
    title: 'Scale Your Business.',
    highlight: 'Multiple queues & providers',
    description:
      'Create multiple queues, assign providers, manage schedules, and handle walk-in customers with QR code scanning.',
  },
];

interface LocationState {
  phoneNumber: string;
  countryCode: string;
}

const OTPScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  const { verifyOTP, resendOTP, setLoginDetails, getSetupStatus, getUserRole } = useAuthService();

  const [otp, setOtp] = useState<string>('');
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [isResending, setIsResending] = useState<boolean>(false);
  const [timer, setTimer] = useState<number>(OTP_RESEND_TIMER);
  const [canResend, setCanResend] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [showSnackbar, setShowSnackbar] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [currentSlide, setCurrentSlide] = useState(0);

  const phoneNumber = state?.phoneNumber || '';
  const countryCode = state?.countryCode || '+91';

  // Auto-rotate slides
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % SLIDES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!phoneNumber) {
      navigate('/login');
    }
  }, [phoneNumber, navigate]);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setCanResend(true);
    }
  }, [timer]);

  const formatPhoneNumber = (phone: string, code: string): string => {
    return `${code} ${phone}`;
  };

  const handleOTPChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/[^0-9]/g, '').slice(0, 6);
    setOtp(value);
    setError('');
  }, []);

  const handleVerifyOTP = useCallback(async () => {
    if (otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const response = await verifyOTP(phoneNumber, countryCode, otp);

      setLoginDetails(
        response.data.access_token,
        response.data.refresh_token,
        response.data.user
      );

      // Post-OTP routing — mirrors the mobile app (which works correctly):
      // the user's ROLE is the primary signal. getUserRole() also persists role +
      // permissions to localStorage (needed by the console shell's RBAC). If the
      // role carries a business_id, the user already belongs to a business
      // (owner OR invited provider/supervisor/admin) -> go straight to the console.
      // Only when there is no business do we fall back to setup-status, and only a
      // genuine "no business" sends the user to onboarding.
      // Use { replace: true } so /otp is removed from history — once verified the
      // user must not be able to navigate Back into the auth flow.
      try {
        const roleResponse = await getUserRole().catch(() => null);

        // Access control: the web console is for Administrator, Admin and
        // Provider roles only. Customers and Supervisors are not allowed in.
        const userType = response.data.user?.user_type;
        const roleName = roleResponse?.data?.role_name;
        if (userType === 'customer' || roleName === 'supervisor') {
          clearSession();
          setError('This portal is for providers and administrators only. Supervisors and customers cannot sign in here.');
          setIsVerifying(false);
          return;
        }

        if (roleResponse?.data?.business_id) {
          navigate(landingPath(), { replace: true });
          return;
        }

        const setupResponse = await getSetupStatus();
        navigate(setupResponse.data.has_business ? landingPath() : '/search-business', { replace: true });
      } catch (routingError) {
        console.error('Post-OTP routing check failed:', routingError);
        navigate('/search-business', { replace: true });
      }
    } catch (err: any) {
      console.error('Verify OTP error:', err);
      const errorMessage = err?.response?.data?.message || 'Invalid OTP. Please try again.';
      setError(errorMessage);
      setOtp('');
    } finally {
      setIsVerifying(false);
    }
  }, [otp, phoneNumber, countryCode, verifyOTP, setLoginDetails, getSetupStatus, getUserRole, navigate]);

  const handleResend = useCallback(async () => {
    setIsResending(true);
    try {
      await resendOTP(phoneNumber, countryCode);

      setTimer(OTP_RESEND_TIMER);
      setCanResend(false);
      setOtp('');
      setError('');

      setSnackbarMessage('OTP sent successfully!');
      setSnackbarSeverity('success');
      setShowSnackbar(true);
    } catch (err: any) {
      console.error('Resend OTP error:', err);
      const errorMessage = err?.response?.data?.message || 'Failed to resend OTP. Please try again.';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setShowSnackbar(true);
    } finally {
      setIsResending(false);
    }
  }, [phoneNumber, countryCode, resendOTP]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && otp.length === 6 && !isVerifying) {
        handleVerifyOTP();
      }
    },
    [otp, isVerifying, handleVerifyOTP]
  );

  const slide = SLIDES[currentSlide];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Left Side — Image Carousel */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          display: { xs: 'none', md: 'block' },
        }}
      >
        {SLIDES.map((s, index) => (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url(${s.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: index === currentSlide ? 1 : 0,
              transition: 'opacity 0.8s ease-in-out',
            }}
          />
        ))}

        {/* Dark Overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 100%)',
          }}
        />

        {/* Tag Pill */}
        <Box
          sx={{
            position: 'absolute',
            top: 32,
            left: 32,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(10px)',
            borderRadius: '24px',
            px: 2.5,
            py: 1,
          }}
        >
          <Box component="img" src="/logo192.png" alt="Logo" sx={{ width: 32, height: 32, borderRadius: '50%' }} />
          <Typography sx={{ color: '#fff', fontWeight: 500, fontSize: 14 }}>
            {slide.tag}
          </Typography>
        </Box>

        {/* Bottom Content */}
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 4, pb: 5 }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 32, lineHeight: '40px', mb: 0.5 }}>
            {slide.title}
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 32,
              lineHeight: '40px',
              mb: 1.5,
              background: 'linear-gradient(90deg, #60A5FA, #34D399)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {slide.highlight}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 15, lineHeight: '24px', maxWidth: 500, mb: 3 }}>
            {slide.description}
          </Typography>

          {/* Slide Indicators */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {SLIDES.map((_, index) => (
              <IconButton key={index} onClick={() => setCurrentSlide(index)} sx={{ p: 0 }}>
                <Box
                  sx={{
                    width: index === currentSlide ? 32 : 20,
                    height: 6,
                    borderRadius: '3px',
                    backgroundColor: index === currentSlide ? '#34D399' : 'rgba(255,255,255,0.4)',
                    transition: 'all 0.3s ease',
                  }}
                />
              </IconButton>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Right Side — OTP Form */}
      <Box
        sx={{
          width: { xs: '100%', md: '40%' },
          minWidth: { md: 400 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, md: 5 },
          py: 4,
          backgroundColor: colors.backgroundPaper,
        }}
      >
        {/* Logo */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box component="img" src="/logo192.png" alt="Logo" sx={{ width: 60, height: 60, borderRadius: '50%', mx: 'auto', mb: 1 }} />
          <Typography sx={{ color: colors.textPrimary, fontWeight: 700, fontSize: 14, letterSpacing: 1 }}>
            BASEQUEUE PROVIDER
          </Typography>
        </Box>

        {/* Title */}
        <Typography
          sx={{ color: colors.textPrimary, fontWeight: 700, fontSize: 28, lineHeight: '36px', mb: 0.5, textAlign: 'center' }}
        >
          Enter OTP
        </Typography>
        <Typography sx={{ color: colors.textSecondary, fontSize: 15, mb: 1, textAlign: 'center' }}>
          We've sent a 6-digit code to {formatPhoneNumber(phoneNumber, countryCode)}
        </Typography>

        {/* Edit number link */}
        <Link
          to="/login"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            color: colors.primary,
            textDecoration: 'none',
            fontWeight: 500,
            fontSize: 14,
            marginBottom: 24,
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} />
          Edit Mobile Number
        </Link>

        {/* OTP Input */}
        <Box sx={{ width: '100%', maxWidth: 380 }}>
          <Typography sx={{ color: colors.gray700, fontWeight: 500, fontSize: 14, mb: 1 }}>
            OTP Code
          </Typography>

          <TextField
            fullWidth
            value={otp}
            onChange={handleOTPChange}
            onKeyDown={handleKeyDown}
            placeholder="Enter 6-digit OTP"
            error={!!error}
            helperText={error}
            disabled={isVerifying}
            slotProps={{
              htmlInput: {
                maxLength: 6,
                style: {
                  textAlign: 'left',
                  letterSpacing: '2px',
                  fontSize: '20px',
                  fontWeight: 600,
                },
              },
            }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                height: 52,
                borderRadius: '12px',
                backgroundColor: colors.backgroundDefault,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
              },
              '& .MuiOutlinedInput-input': {
                padding: '14px 16px',
                '&::placeholder': { color: '#94A3B8', opacity: 1 },
              },
            }}
          />

          {/* Verify Button */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleVerifyOTP}
            disabled={isVerifying || otp.length !== 6}
            sx={{
              height: 52,
              borderRadius: '12px',
              background: otp.length === 6
                ? `linear-gradient(90deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`
                : colors.gray200,
              color: otp.length === 6 ? '#fff' : colors.textTertiary,
              fontWeight: 600,
              fontSize: 16,
              textTransform: 'none',
              boxShadow: otp.length === 6 ? '0 4px 14px rgba(7, 104, 223, 0.3)' : 'none',
              mb: 2,
              '&:hover': {
                background: otp.length === 6
                  ? `linear-gradient(90deg, ${colors.primaryDark} 0%, ${colors.primary} 100%)`
                  : colors.gray200,
              },
              '&.Mui-disabled': {
                backgroundColor: colors.gray200,
                color: colors.textTertiary,
              },
            }}
          >
            {isVerifying ? <CircularProgress size={24} color="inherit" /> : 'Verify OTP'}
          </Button>

          {/* Resend */}
          <Box sx={{ textAlign: 'center' }}>
            {canResend ? (
              <Button
                onClick={handleResend}
                disabled={isResending}
                sx={{
                  color: colors.primary,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: 15,
                  '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
                }}
              >
                {isResending && <CircularProgress size={16} sx={{ mr: 1 }} />}
                Resend OTP
              </Button>
            ) : (
              <Typography sx={{ color: colors.textSecondary, fontSize: 15 }}>
                Resend OTP in {timer}s
              </Typography>
            )}
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography sx={{ color: colors.textTertiary, fontSize: 12 }}>
            Powered by{' '}
            <Box component="span" sx={{ color: colors.primary, fontWeight: 600 }}>
              BaseQueue Provider
            </Box>
          </Typography>
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OTPScreen;
